import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { exportCSV } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Users, Download } from "lucide-react";

const fmt = (iso, withYear = false) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", ...(withYear ? { year: "numeric" } : {}), hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

export function TeamAccessCard() {
  const [managers, setManagers] = useState([]);
  const [managerId, setManagerId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/admin/managers").then((r) => setManagers(r.data)).catch(() => setManagers([]));
  }, []);

  useEffect(() => {
    if (!managerId) return undefined;
    let cancelled = false;
    const load = () => {
      api.get(`/admin/security/team-access?manager_id=${managerId}`)
        .then((r) => { if (!cancelled) setData(r.data); })
        .catch((e) => { if (!cancelled) toast.error(formatApiError(e.response?.data?.detail)); })
        .finally(() => { if (!cancelled) setLoading(false); });
    };
    setLoading(true);
    load();
    const id = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [managerId]);

  const doExport = () => {
    if (!data) return;
    const rows = data.logins.map((l) => ({
      "Capo Area": data.manager.full_name, Utente: l.full_name, Email: l.email, Ruolo: l.role === "manager" ? "Capo Area" : "Agente",
      "Data / ora": fmt(l.at, true), IP: l.ip,
    }));
    if (!rows.length) { toast.info("Nessun accesso da esportare"); return; }
    exportCSV(`accessi_${data.manager.full_name.replace(/\s+/g, "_")}.csv`, rows);
    toast.success("CSV esportato con successo");
  };

  return (
    <Card className="bg-white border-[#DCE7F5] overflow-hidden" data-testid="team-access-card">
      <div className="p-5 border-b border-[#DCE7F5] flex items-center justify-between gap-3 flex-wrap">
        <div>
          <span className="font-display font-semibold text-[#0F1E33] flex items-center gap-2">
            <Users className="w-4 h-4 text-[#0B6BCB]" strokeWidth={1.5} /> Accessi per Capo Area
          </span>
          <p className="text-xs text-[#5A6B85] mt-1">Storico accessi del Capo Area e di tutti i suoi agenti</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={managerId} onValueChange={setManagerId}>
            <SelectTrigger className="w-[240px] bg-white border-[#DCE7F5] h-9" data-testid="team-access-manager-select">
              <SelectValue placeholder="Seleziona Capo Area" />
            </SelectTrigger>
            <SelectContent className="bg-white border-[#DCE7F5]">
              {managers.map((m) => (
                <SelectItem key={m.id} value={m.id} data-testid={`team-access-manager-option-${m.id}`}>{m.full_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={doExport} disabled={!data} className="border-[#DCE7F5] bg-transparent hover:bg-[#EEF4FC] text-[#0F1E33] disabled:opacity-40" data-testid="team-access-export-btn">
            <Download className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Esporta CSV
          </Button>
        </div>
      </div>

      {!managerId && (
        <div className="text-center text-[#5A6B85] py-10 text-sm" data-testid="team-access-empty">Seleziona un Capo Area per vedere gli accessi del suo team</div>
      )}
      {managerId && loading && !data && <div className="p-5"><Skeleton className="h-40 bg-[#EEF4FC]" /></div>}
      {managerId && data && (
        <>
          <div className="overflow-x-auto border-b border-[#DCE7F5]">
            <Table>
              <TableHeader>
                <TableRow className="border-[#DCE7F5] hover:bg-transparent">
                  <TableHead className="text-[#5A6B85]">Utente</TableHead>
                  <TableHead className="text-[#5A6B85]">Ruolo</TableHead>
                  <TableHead className="text-[#5A6B85] text-center">Accessi totali</TableHead>
                  <TableHead className="text-[#5A6B85]">Primo accesso</TableHead>
                  <TableHead className="text-[#5A6B85]">Ultimo accesso</TableHead>
                  <TableHead className="text-[#5A6B85] text-center">Connesso ora</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.users.map((u) => (
                  <TableRow key={u.id} className={`border-[#DCE7F5] ${u.role === "manager" ? "bg-[#F5F9FF]" : ""}`} data-testid={`team-access-user-row-${u.id}`}>
                    <TableCell>
                      <div className="font-medium text-[#0F1E33]">{u.full_name}</div>
                      <div className="text-xs text-[#94A3B8]">{u.email}</div>
                    </TableCell>
                    <TableCell>
                      <Badge className={u.role === "manager" ? "bg-[#0B6BCB] text-white border-transparent" : "bg-[#EEF4FC] text-[#0B6BCB] border-[#DCE7F5]"}>
                        {u.role === "manager" ? "Capo Area" : "Agente"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center font-display font-semibold text-[#0F1E33]" data-testid={`team-access-count-${u.id}`}>{u.login_count}</TableCell>
                    <TableCell className="text-[#5A6B85] text-sm">{fmt(u.first_login, true)}</TableCell>
                    <TableCell className="text-[#5A6B85] text-sm">{u.last_login ? fmt(u.last_login, true) : <span className="text-amber-600">Mai entrato</span>}</TableCell>
                    <TableCell className="text-center">
                      {u.active_sessions > 0 ? (
                        <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700"><span className="w-2 h-2 rounded-full bg-emerald-500" /> {u.active_sessions} disp.</span>
                      ) : <span className="text-xs text-[#94A3B8]">No</span>}
                    </TableCell>
                  </TableRow>
                ))}
                {data.users.length <= 1 && (
                  <TableRow><TableCell colSpan={6} className="text-center text-[#5A6B85] py-4 text-sm">Nessun agente assegnato a questo Capo Area</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <div className="p-5 pb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-[#0F1E33]">Cronologia accessi</span>
            <span className="text-xs text-[#5A6B85]" data-testid="team-access-logins-total">{data.logins.length} accessi</span>
          </div>
          <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-[#DCE7F5] hover:bg-transparent">
                  <TableHead className="text-[#5A6B85]">Utente</TableHead>
                  <TableHead className="text-[#5A6B85]">Ruolo</TableHead>
                  <TableHead className="text-[#5A6B85]">IP</TableHead>
                  <TableHead className="text-[#5A6B85]">Data / ora</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.logins.map((l) => (
                  <TableRow key={l.id} className="border-[#DCE7F5]" data-testid={`team-access-login-row-${l.id}`}>
                    <TableCell className="font-medium text-[#0F1E33]">{l.full_name}</TableCell>
                    <TableCell className="text-[#5A6B85] text-sm">{l.role === "manager" ? "Capo Area" : "Agente"}</TableCell>
                    <TableCell className="text-[#5A6B85] text-sm">{l.ip}</TableCell>
                    <TableCell className="text-[#5A6B85] text-sm">{fmt(l.at, true)}</TableCell>
                  </TableRow>
                ))}
                {data.logins.length === 0 && (
                  <TableRow><TableCell colSpan={4} className="text-center text-[#5A6B85] py-8">Nessun accesso registrato per questo team</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </Card>
  );
}
